/* ============================================================
   Shop types — seller catalog, cart, checkout
   Mirrors backend ProductDtos/CartDtos.
   ============================================================ */

export type ProductStatus = 'Draft' | 'Active' | 'Archived' | 0 | 1 | 2;

/** 423ee061: paged seller's own listings (status filter + title search server-side). */
export interface ProductMinePage {
  total: number;
  offset: number;
  limit: number;
  items: ProductDetail[];
}

/** ME-3: one gallery item (mirrors backend ProductMediaDto). */
export interface ProductMediaItem {
  url: string;
  thumbnailUrl: string | null;
  kind: 'image' | 'video';
}

export interface ProductSummary {
  productId: string;
  title: string;
  imageUrl: string | null;
  /** ME-3: preferred card cover (first image thumb, may fall back to imageUrl). */
  coverUrl: string | null;
  materialName: string;
  color: string;
  priceRub: number;
  compareAtPriceRub: number | null;
  providerName: string;
  stockLeft: number | null;
  productionDays: number;
  /** 3d4c488f: shop taxonomy node (null = uncategorized). */
  categoryId: string | null;
  categoryName: string | null;
  /** a1a3b8f4: seller-defined priced options (public view: enabled only; null = none). */
  options: ProductOption[] | null;
}

/** a1a3b8f4: value of a Choice option. */
export interface ProductOptionValue {
  valueId: string;
  label: string;
  priceRub: number;
}

/** a1a3b8f4: seller-defined priced option (type 0 bool / 1 choice / 2 quantity; priceMode 0 fixed / 1 perUnit). */
export interface ProductOption {
  optionId: string;
  name: string;
  type: number;
  priceMode: number;
  priceRub: number;
  enabled: boolean;
  position: number;
  values: ProductOptionValue[] | null;
}

/** a1a3b8f4: option input on create/update (replace-all; ids reassigned server-side). */
export interface ProductOptionDraft {
  name: string;
  type: number;
  priceMode: number;
  priceRub: number;
  enabled: boolean;
  values: { label: string; priceRub: number }[] | null;
}

export interface ProductDetail {
  productId: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  /** ME-3: gallery media in seller-defined order. */
  media: ProductMediaItem[] | null;
  materialId: string | null;
  materialName: string;
  color: string;
  priceRub: number;
  compareAtPriceRub: number | null;
  providerId: string;
  providerName: string;
  stockLeft: number | null;
  minOrderQuantity: number;
  maxOrderQuantity: number | null;
  productionDays: number;
  status: ProductStatus;
  isPublished: boolean;
  createdAtUtc: string;
  /** 3d4c488f: shop taxonomy node (null = uncategorized). */
  categoryId: string | null;
  categoryName: string | null;
  /** a1a3b8f4: seller-defined priced options (public view: enabled only; null = none). */
  options: ProductOption[] | null;
}

export interface ProductDraft {
  title: string;
  description: string | null;
  imageUrl: string | null;
  /** ME-3: gallery items (engine upload outputs); null/empty clears the gallery. */
  media: ProductMediaItem[] | null;
  materialId: string | null;
  color: string | null;
  priceRub: number;
  compareAtPriceRub: number | null;
  stockCount: number | null;
  minOrderQuantity: number;
  maxOrderQuantity: number | null;
  productionDays: number;
  publish: boolean;
  /** 3d4c488f: shop taxonomy node assignment (null clears). */
  productCategoryId: string | null;
  /** a1a3b8f4: priced options replace-all (null clears). */
  options: ProductOptionDraft[] | null;
}

export interface CartItemView {
  itemId: string;
  productId: string;
  title: string;
  imageUrl: string | null;
  unitPriceRub: number;
  quantity: number;
  lineTotalRub: number;
  providerName: string;
  quantityError: string | null;
  /** MS-2: cart line kind (numeric CartItemKind — 0 SellerProduct, 1 QuotePrint);
   * defensive optional for older payloads / product-only carts. */
  kind?: number | string | null;
  /** 3830fe0d: quote-print lines carry their performer + per-line delivery
   * choice (CartItemDto.PerformerId / DeliveryMethod mirror). */
  performerId?: string | null;
  /** 2026-09-03: the performer's city — checkout courier rule (courier only
   * same-city from a single performer). */
  providerCity?: string | null;
  deliveryMethod?: WebDeliveryMethod | number | null;
  /** 31c9ace7: carrier behind the line's live delivery tariff («СДЭК» …); null/undefined = static/pickup line. */
  deliveryCarrier?: string | null;
  /** MS-3 (TASK-bfa93913): in-stock colors of this line's material at its
   * performer (most-remaining first). null = performer does not track stock
   * (standard palette fallback); [] = tracking but sold out (уточните);
   * undefined = old payload / product line. */
  stockColors?: string[] | null;
  /** ecfe2257: stockColors with the performer's hex swatch per color (same order); null = not tracking. */
  stockSwatches?: ColorSwatch[] | null;
  /** MS-3: rendered-model preview for quote-print lines (ME-2 PreviewUrl). */
  previewUrl?: string | null;
  /** cc4909b4: the line's uploaded model id — mounts the interactive 3D viewer. */
  modelFileId?: string | null;
  /** cc4909b4: mesh format (0 STL / 1 OBJ / 2 3MF live; 3 STEP → PNG fallback). */
  modelFileFormat?: number | string | null;
  /** a1a3b8f4: display text of the chosen product options (seller-product lines). */
  optionsSummary?: string | null;
}

/** ecfe2257: one offered color + its optional #RRGGBB swatch value. */
export interface ColorSwatch {
  color: string;
  hexColor?: string | null;
}

export interface Cart {
  items: CartItemView[];
  subtotalRub: number;
}

/** Mirrors backend Domain OrderStatus — full 8-value set incl. TASK-234 Printed/Disputed. */
export type WebOrderStatus =
  | 'Pending' | 'Accepted' | 'Printing' | 'Printed'
  | 'Shipped' | 'Completed' | 'Cancelled' | 'Disputed';

/** G8-D1: mirrors backend Domain DeliveryMethod (numeric JSON serialization).
 * Pickup = самовывоз (0 ₽), Courier = курьер (static 350/600 ₽ table). */
export type WebDeliveryMethod = 'Pickup' | 'DropoffPoint' | 'Courier';

const DELIVERY_METHOD_BY_INDEX: readonly WebDeliveryMethod[] = ['Pickup', 'DropoffPoint', 'Courier'];

const DELIVERY_METHOD_TO_INDEX: Readonly<Record<WebDeliveryMethod, number>> = {
  Pickup: 0, DropoffPoint: 1, Courier: 2,
};

/** Wire value for the API's numeric enum serialization (G8-D1). */
export function deliveryMethodToJson(method: WebDeliveryMethod): number {
  return DELIVERY_METHOD_TO_INDEX[method] ?? 2;
}

export function normalizeDeliveryMethod(method: WebDeliveryMethod | number | null | undefined): WebDeliveryMethod {
  if (typeof method === 'number') {
    return DELIVERY_METHOD_BY_INDEX[method] ?? 'Courier';
  }
  return method ?? 'Courier';
}

/** G8-D1: static price-table constants (mirror PerformerMatchingService) for chip labels. */
export const SAME_CITY_DELIVERY_RUB = 350;

/** G8-D2: public self-pickup point of a provider (address-only DTO). */
export interface PublicPickupPoint {
  pickupPointId: string;
  address: string;
  workingHours: string | null;
  /** 2026-09-03 (Артем): standardized map fields — the checkout map renders
   * the performer's own points as their own orange layer. */
  city?: string | null;
  postalCode?: string | null;
  geoLat?: string | null;
  geoLon?: string | null;
}

/** Human method label (G8-D1 chips/rows). */
export const DELIVERY_METHOD_LABELS: Readonly<Record<WebDeliveryMethod, string>> = {
  Pickup: 'Самовывоз',
  DropoffPoint: 'Пункт выдачи',
  Courier: 'Курьер',
};
/**
 * The API serializes OrderStatus as a JSON number (System.Text.Json default,
 * no string converter wired in Program.cs). Enum ordering is historical —
 * Disputed=6, Printed=7 (TASK-234) — never renumber.
 */
const ORDER_STATUS_BY_INDEX: readonly WebOrderStatus[] = [
  'Pending', 'Accepted', 'Printing', 'Shipped', 'Completed', 'Cancelled', 'Disputed', 'Printed',
];

/** Wire value for the API's numeric OrderStatus serialization (inverse of ORDER_STATUS_BY_INDEX). */
const ORDER_STATUS_TO_INDEX: Readonly<Record<WebOrderStatus, number>> = {
  Pending: 0, Accepted: 1, Printing: 2, Shipped: 3,
  Completed: 4, Cancelled: 5, Disputed: 6, Printed: 7,
};

/** The API expects the numeric enum on write (no string converter in Program.cs) — TASK-254 fix. */
export function orderStatusToJson(status: WebOrderStatus): number {
  return ORDER_STATUS_TO_INDEX[status] ?? 0;
}

export function normalizeOrderStatus(status: WebOrderStatus | number): WebOrderStatus {
  if (typeof status === 'number') {
    return ORDER_STATUS_BY_INDEX[status] ?? 'Pending';
  }
  return status;
}

/** TASK-243 (G6): SLA/overdue snapshot for the current order stage (mirrors OrderSlaDto). */
export interface OrderSla {
  stage: 'ProviderResponse' | 'ProviderResult' | 'CustomerConfirmation' | 'Delivery' | string;
  state: 'OnTrack' | 'DueSoon' | 'Overdue' | string;
  deadlineUtc: string;
  daysOverdue: number;
}

/** TASK-246: mirrors backend CounterOfferStatus (serialized as JSON number). */
export type WebCounterOfferStatus = 'Pending' | 'Accepted' | 'Rejected';

const COUNTER_OFFER_STATUS_BY_INDEX: readonly WebCounterOfferStatus[] = [
  'Pending', 'Accepted', 'Rejected',
];

export function normalizeCounterOfferStatus(status: WebCounterOfferStatus | number): WebCounterOfferStatus {
  if (typeof status === 'number') {
    return COUNTER_OFFER_STATUS_BY_INDEX[status] ?? 'Pending';
  }
  return status;
}

/** TASK-246: one negotiation offer (mirrors CounterOfferDto). */
export interface CounterOffer {
  offerId: string;
  orderId: string;
  proposedByUserId: string;
  isFromProvider: boolean;
  proposedPriceRub: number;
  proposedDeliveryUtc: string | null;
  message: string;
  status: WebCounterOfferStatus | number;
  createdAtUtc: string;
  respondedAtUtc: string | null;
}

export interface OrderSummary {
  orderId: string;
  status: WebOrderStatus | number;
  totalPrice: number;
  platformFee: number;
  providerId?: string | null;
  createdAtUtc: string;
  estimatedDeliveryUtc: string | null;
  completedAtUtc: string | null;
  trackingNumber: string | null;
  sla: OrderSla | null;
  // TASK-255: list-row cue parity with the detail view / provider queue.
  progressPhotoCount?: number;
  resultPhotoCount?: number;
  customerConfirmedAtUtc?: string | null;
  disputedAtUtc?: string | null;
  /** audit4-A2: when silence auto-confirms the result (P2 basis + ConfirmTimeoutHours). Null outside Printed-unconfirmed. */
  confirmDeadlineUtc?: string | null;
  /** SD-2 (e0a66625): YooKassa redirect attached at checkout while the Safe Deal rail is on; null while disabled/failed. SD-3: the placed-screen pay CTA. */
  paymentConfirmationUrl?: string | null;
  /** TASK-256 (§92): G8-D1 delivery-method snapshot (numeric enum on the wire; Courier default = legacy rows) — feeds the method-aware SlaBadge on list rows. */
  deliveryMethod?: WebDeliveryMethod | number;
  /** 31c9ace7: carrier behind the checkout delivery tariff — authoritative label/deep-link for the shipped tracking row (null = legacy, shape-detect fallback). */
  deliveryCarrier?: string | null;
  /** 10fb5d39 (audit-11 §5 Card B): counterparty chat messages waiting after
   *  the customer's read cursor — the list row's 💬 unread cue. Absent on
   *  legacy payloads; 0 = nothing waiting. */
  unreadChatMessages?: number;
  /** 4171ba09: provider display name — the slim paginated row's counterparty
   * line (batch-resolved server-side per page; null on legacy/unassigned rows). */
  providerName?: string | null;
}

/** 4171ba09: one page of customer order rows + the filter-chip counts over
 * the whole history (not just this page) — mirrors OrderSummaryPageDto. */
export interface OrderSummaryPage {
  total: number;
  offset: number;
  limit: number;
  bucketCounts: Record<string, number>;
  items: OrderSummary[];
}

/** Mirrors backend OrderKind. */
export type OrderKind = 'CustomQuote' | 'SellerProducts' | 'PerformerCart';

/** Placed order line: seller product or quote-print job (productId null for quote prints). */
export interface OrderItem {
  productId: string | null;
  productTitle: string;
  unitPriceRub: number;
  quantity: number;
  lineTotalRub: number;
  /** a1a3b8f4: display text of the chosen product options (empty = none). */
  optionsSummary?: string | null;
}

/** TASK-235: photo proof of the finished print (mirrors OrderResultPhotoDto).
 *  TASK-250: kind — 0 = final result proof, 1 = in-progress update. */
export interface OrderResultPhoto {
  photoId: string;
  url: string;
  caption: string | null;
  createdAtUtc: string;
  kind?: 0 | 1;
}

/** TASK-235: full customer order detail (mirrors OrderDetailDto from TASK-234). */
export interface OrderDetail {
  orderId: string;
  status: WebOrderStatus | number;
  totalPrice: number;
  platformFee: number;
  createdAtUtc: string;
  estimatedDeliveryUtc: string | null;
  completedAtUtc: string | null;
  trackingNumber: string | null;
  shippingAddress: string | null;
  notes: string | null;
  modelFileName: string;
  materialName: string;
  estimatedPrintMinutes: number;
  estimatedMaterialGrams: number;
  providerName: string | null;
  allowedTransitions: string[];
  kind: OrderKind | number;
  items: OrderItem[];
  resultNote: string | null;
  customerConfirmedAtUtc: string | null;
  /** TASK-251: when the dispute was opened (timeline terminal state). */
  disputedAtUtc?: string | null;
  /** TASK-667a55be: the customer's own dispute reason, echoed on the Disputed cue. */
  disputeReason?: string | null;
  /** TASK-667a55be: support resolution note + stamp — dispute outcome after admin resolve. */
  disputeResolutionNote?: string | null;
  disputeResolvedAtUtc?: string | null;
  resultPhotos: OrderResultPhoto[];
  sla: OrderSla | null;
  /** TASK-246: negotiation history, newest first (custom-quote orders). */
  counterOffers?: CounterOffer[];
  /** G8-D1: delivery method snapshot (numeric enum on the wire; Courier for legacy rows). */
  deliveryMethod?: WebDeliveryMethod | number;
  /** G8-D1: delivery cost snapshot (0 for pickup); 0 on legacy courier rows. */
  deliveryCostRub?: number;
  /** 31c9ace7: carrier behind the checkout delivery tariff («СДЭК» …); null = static/legacy — tracking row falls back to shape detection. */
  deliveryCarrier?: string | null;
  /** audit4-A2: when silence auto-confirms the result (P2 basis + ConfirmTimeoutHours). Null outside Printed-unconfirmed. */
  confirmDeadlineUtc?: string | null;
  /** SD-3 (d36195ae): Safe Deal paid stamp (payment.succeeded webhook). */
  paidAtUtc?: string | null;
  /** SD-3 (d36195ae): null hides the whole pay UI (rail off / no payment applies); awaiting_payment = pay/retry CTA; paid = escrow chip; SD-5 (TASK-20ad2261): refunded = «money returned» chip. Backend-derived — never fee math. */
  paymentState?: SafeDealOrderPaymentState | null;
  /** ME-2: rendered preview of the uploaded model (/media/model-previews/…) on the customer detail; null for seller-product orders or unrenderable models. */
  modelPreviewUrl?: string | null;
  /** cc4909b4: the uploaded model's id — mounts the interactive 3D viewer on the detail. */
  modelFileId?: string | null;
  /** cc4909b4: mesh format (0 STL / 1 OBJ / 2 3MF live; 3 STEP → PNG fallback). */
  modelFileFormat?: number | string | null;
  /** MS-1 (TASK-52194f26): the print color chosen at checkout (snapshot; null when none was picked). */
  printColor?: string | null;
  /** ecfe2257: 0 any / 1 multicolor (уточняется в переписке) / 2 concrete. */
  printColorMode?: number;
}

/** SD-3/SD-5: mirrors backend OrderPaymentState strings (stable contract, no numeric enum). */
export type SafeDealOrderPaymentState = 'awaiting_payment' | 'paid' | 'refunded';

/** SD-3 (d36195ae): mirrors SafeDealPaymentInitiationDto — the redirect result of POST /orders/{id}/pay. */
export interface SafeDealPaymentInitiation {
  orderId: string;
  dealId: string;
  paymentId: string;
  confirmationUrl: string | null;
  settlementPayoutRub: number;
}

/** CB-5: outcome of re-adding one Completed-order line to the cart (mirrors RepeatItemOutcomeDto). */
export interface RepeatItemOutcome {
  itemId: string;
  title: string;
  added: boolean;
  skipReason: string | null;
}

/** CB-5: result of POST /api/v1/orders/{id}/repeat (mirrors RepeatOrderResultDto). */
export interface RepeatOrderResult {
  addedCount: number;
  skippedCount: number;
  items: RepeatItemOutcome[];
}

/** 054819ad: public product review (display-only v1, mirrors ProductReviewDto). */
export interface ProductReviewSummary {
  id: string;
  productId: string;
  authorName: string;
  rating: number;
  text: string;
  createdAtUtc: string;
}

/** 0aa086dd: created product review incl. moderation status (write-path echo of ProductReviewDto). */
export interface ProductReview extends ProductReviewSummary {
  status: number;
}

/** 054819ad: mirrors ProductReviewsPageDto — published items + aggregate over the same set. */
export interface ProductReviewsPage {
  averageRating: number | null;
  reviewCount: number;
  items: ProductReviewSummary[];
}

export const formatRub = (v: number): string =>
  `${v.toLocaleString('ru-RU', { maximumFractionDigits: 0 })} ₽`;
